import {CheckoutSettingsModel} from '../../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';
import {
  AddressTarget,
  FieldPropertiesByTarget,
  FormFieldTarget,
  BillingFormOverrides,
} from '../../../../types/checkoutApp.types';
import {
  getExcludeGoogleAutoCompleteOverrides,
  getSettingsOverrides,
  mergeOverrides,
  getLabelsOverrides,
  mandatoryOverride,
  getAddressFormInitialState,
  getContactFormInitialState,
} from '../form.utils';
import {ILocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {ContactModel} from '../../../../domain/models/checkout/Cart/Contact.model';
import {AddressModel} from '../../../../domain/models/checkout/Cart/Address.model';
import {FormValues, VatIDValue} from '@wix/form-fields';
import {type CountryCode} from '@wix/form-multiline-address';
import {isCountryWithVat} from '../../../../domain/utils/isCountryWithVat';
import {WixCommonVatType} from '../../../../gql/graphql';
import {CashierMandatoryField, CashierMandatoryFieldsOverrides} from '../../../../types/payment.types';
import {FormOverride} from '@wix/form-viewer/dist/types/types';
import {VatIdModel} from '../../../../domain/models/checkout/Cart/VatId.model';
import {getVatType} from '../../../../domain/utils/vat.util';

const cashierFieldToAddressTarget = {
  [CashierMandatoryField.address]: AddressTarget.address_line,
  [CashierMandatoryField.zipCode]: AddressTarget.postal_code,
  [CashierMandatoryField.countryCode]: AddressTarget.country,
  [CashierMandatoryField.houseNumber]: AddressTarget.street_number,
  [CashierMandatoryField.street]: AddressTarget.street_name,
  [CashierMandatoryField.state]: AddressTarget.subdivision,
  [CashierMandatoryField.city]: AddressTarget.city,
};

function mapCashierMandatoryFieldToOverride(
  cashierMandatoryField: CashierMandatoryField,
  mandatory?: boolean
): BillingFormOverrides | undefined {
  /* istanbul ignore next */
  if (!mandatory) {
    return {};
  }
  switch (cashierMandatoryField) {
    case CashierMandatoryField.address:
    case CashierMandatoryField.city:
    case CashierMandatoryField.countryCode:
    case CashierMandatoryField.houseNumber:
    case CashierMandatoryField.state:
    case CashierMandatoryField.street:
    case CashierMandatoryField.zipCode: {
      const target = cashierFieldToAddressTarget[cashierMandatoryField];
      return {
        address: {
          [target]: mandatoryOverride,
        },
      };
    }
    case CashierMandatoryField.phone:
      return {
        phone: mandatoryOverride,
      };
    /* istanbul ignore next */
    default:
      /* istanbul ignore next */
      return undefined;
  }
}

export function getCashierOverrides(cashierOverrides: CashierMandatoryFieldsOverrides): BillingFormOverrides {
  const fieldTargets = Object.keys(cashierOverrides) as (keyof typeof cashierOverrides)[];

  return fieldTargets.reduce((accOverrides, fieldTarget) => {
    const fieldOverride = mapCashierMandatoryFieldToOverride(fieldTarget, cashierOverrides[fieldTarget]);
    /* istanbul ignore next */
    if (!fieldOverride) {
      return accOverrides;
    }
    return mergeOverrides(accOverrides, fieldOverride);
  }, {});
}

function mapFormFieldPropsToOverrides(props: FieldPropertiesByTarget[string]): FormOverride {
  if (!props) {
    return {
      hidden: true,
      required: false,
    };
  }
  /* istanbul ignore else */
  if (typeof props?.hidden === 'boolean' && typeof props?.required === 'boolean') {
    return {
      hidden: props.hidden,
      required: props.required,
    };
  }
  /* istanbul ignore next */
  return {};
}

function getVatFormInitialState(contact?: ContactModel, nullIfMIssing?: boolean) {
  if (nullIfMIssing && !contact?.vatId?.id) {
    return {vat_id: null};
  }

  return {
    vat_id: {
      id: contact?.vatId?.id ?? /* istanbul ignore next */ '',
      type: contact?.vatId?.type ?? /* istanbul ignore next */ WixCommonVatType.UNSPECIFIED,
    },
  };
}

export function getShippingDetailsRelatedFieldsOverrides({
  formFieldsProperties,
}: {
  formFieldsProperties: FieldPropertiesByTarget;
}): BillingFormOverrides {
  const phoneProps = formFieldsProperties[FormFieldTarget.phone];
  const companyProps = formFieldsProperties[FormFieldTarget.company_name];
  const addressProps = formFieldsProperties[FormFieldTarget.address] as FieldPropertiesByTarget | undefined;
  const addressLine2Props = addressProps ? addressProps[AddressTarget.address_line_2] : undefined;

  return {
    phone: mapFormFieldPropsToOverrides(phoneProps),
    company_name: mapFormFieldPropsToOverrides(companyProps),
    address: {
      address_line_2: mapFormFieldPropsToOverrides(addressLine2Props),
    },
  };
}

export function getVatIdOverride(country: CountryCode): BillingFormOverrides {
  return isCountryWithVat(country)
    ? {
        vat_id: {
          hidden: false,
          required: true,
        },
      }
    : {};
}

export function getVatFromVatFormValues(vatFormValues: FormValues | null): VatIdModel | undefined {
  if (!vatFormValues?.vat_id) {
    return undefined;
  }
  const vat = vatFormValues.vat_id as VatIDValue;
  return new VatIdModel({id: vat.id, type: getVatType(vat.type)});
}

export type BillingFormRelevantSettings = Pick<
  CheckoutSettingsModel,
  'enabledDeliveryCountries' | 'isLocalDeliveryByZipCodeEnabled'
>;

export function getBillingFormOverrides({
  cashierMandatoryFields,
  checkoutSettings,
  shippingDetailsFieldsProps,
  excludeGoogleAutoComplete,
  localeKeys,
  country,
  moveCheckoutToViewerContext,
}: {
  cashierMandatoryFields: CashierMandatoryFieldsOverrides;
  checkoutSettings: BillingFormRelevantSettings;
  shippingDetailsFieldsProps: FieldPropertiesByTarget;
  excludeGoogleAutoComplete?: boolean;
  localeKeys?: ILocaleKeys;
  country?: CountryCode | null;
  moveCheckoutToViewerContext: boolean;
}): BillingFormOverrides {
  const cashierMandatoryFieldsOverrides = getCashierOverrides(cashierMandatoryFields);
  const settingsOverrides = getSettingsOverrides({checkoutSettings});
  const shippingDetailsRelatedFieldsOverrides = getShippingDetailsRelatedFieldsOverrides({
    formFieldsProperties: shippingDetailsFieldsProps,
  });
  const hideCountryOverride = {};
  const excludeGoogleAutoCompleteOverrides = getExcludeGoogleAutoCompleteOverrides({
    excludeGoogleAutoComplete,
    country,
    moveCheckoutToViewerContext,
  });
  const labelsOverrides = localeKeys ? getLabelsOverrides({localeKeys}) : /* istanbul ignore next */ {};
  const vatIdOverride = country ? getVatIdOverride(country) : {};

  return mergeOverrides(
    settingsOverrides,
    shippingDetailsRelatedFieldsOverrides,
    hideCountryOverride,
    excludeGoogleAutoCompleteOverrides,
    labelsOverrides,
    vatIdOverride,
    cashierMandatoryFieldsOverrides
  );
}

export function getBillingFormInitialState({
  contact,
  initialAddress,
  billingContact,
}: {
  contact?: ContactModel;
  initialAddress?: AddressModel;
  billingContact?: ContactModel;
}) {
  const country = initialAddress?.country ?? undefined;

  return {
    ...getContactFormInitialState({
      contact,
      country,
    }),
    ...getAddressFormInitialState(initialAddress, country),
    ...getVatFormInitialState(billingContact, true),
  };
}
